import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import type { StarterModule } from '../model/flow';

interface ModulePickerProps {
  modules: StarterModule[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  disabled?: boolean;
}

/**
 * Плитки «другой темы» на экране знакомства: стартовый модуль и альтернативы, выбор одного.
 * Две колонки и на телефоне — семь тем помещаются без длинной прокрутки; у плиток рамка,
 * серое на тёмном без неё терялось.
 */
export function ModulePicker({ modules, selectedId, onSelect, disabled = false }: ModulePickerProps) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-2.5">
      {modules.map((m) => {
        const isSelected = m.id === selectedId;
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => onSelect(m.id)}
            disabled={disabled}
            aria-pressed={isSelected}
            className={cn(
              'group flex min-h-[96px] min-w-0 flex-col rounded-[18px] border-[1.5px] border-solid p-3.5 text-left transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60',
              isSelected ? 'border-accent bg-accent/[0.08]' : 'border-line bg-elev hover:border-muted-2',
            )}
          >
            <span className="mb-1.5 flex items-center justify-between gap-2">
              <span className="rounded-pill bg-base/60 px-2 py-0.5 font-mono text-[12px] font-semibold tracking-[0.08em] text-muted">
                {m.cefrLevel ?? '·'}
              </span>
              <span
                aria-hidden
                className={cn(
                  'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-[1.5px] border-solid transition-colors',
                  isSelected
                    ? 'border-accent bg-accent text-fg-inv'
                    : 'border-line bg-transparent text-transparent group-hover:border-muted',
                )}
              >
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4.2L3.7 7L9 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </span>
            <span className="line-clamp-2 font-serif text-[16px] font-medium leading-snug text-fg [hyphens:auto]">
              {m.name}
            </span>
            {m.wordCount > 0 && (
              <span className="mt-auto pt-1 text-[12px] text-muted">
                {t('onboarding.intro.words', { count: m.wordCount })}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
